import { marketingActions, appDownloadDescription, footerCareDescription } from "@/modules/landing/content/marketingContent";
import Image from "next/image";
import Link from "next/link";
import { CarevincoLogo } from "@/shared/components/CarevincoLogo";
import {
  customerSupportEmail,
  customerSupportEmailHref,
  customerSupportPhone,
  customerSupportTelHref,
  customerSupportWhatsApp,
  customerSupportWhatsAppHref,
  isSiteActive,
} from "@/shared/config/customerSupport";
import { seoLocality } from "@/shared/config/seo";
import styles from "./MarketingFooter.module.scss";

type MarketingFooterProps = {
  appStoreUrl: string;
  googlePlayUrl: string;
  showInquiry?: boolean;
};

const SOCIAL_ICONS = [
  { alt: "Facebook", height: 32, src: "/assets/landingPage/facebook-245f2149-02cf-4a7c-bc8d-2793a32cadd6.png", width: 32 },
  { alt: "Instagram", height: 32, src: "/assets/landingPage/instagram-96325ffc-1bb7-484f-9489-f5b46a89e83c.png", width: 32 },
  { alt: "YouTube", height: 27, src: "/assets/landingPage/you-tube-7c9449af-dad8-4e9a-b1dd-ed29f02484ac.png", width: 39 },
  { alt: "LinkedIn", height: 32, src: "/assets/landingPage/linked-in-b654d211-453b-4490-a861-8265d1d0bd8c.png", width: 32 },
  { alt: "Twitter", height: 24, src: "/assets/landingPage/twitter-0e343ca4-b03f-4607-94a9-e1d04357efcd.png", width: 24 },
] as const;

const SERVICE_CITIES = [seoLocality.displayName] as const;

const PROMISE_KEYWORDS = [
  "Home Nursing Services",
  `Home Nursing in ${seoLocality.displayName}`,
  "Home Nurse",
  "Nursing Care at Home",
  "Elderly Nursing Care",
  "Patient Care at Home",
  "Professional Home Nurses",
  "Home Healthcare",
  "Nursing Services",
  "Post-Hospital Care",
  "Palliative Nursing Care",
  "Home Care Services",
  "Verified Home Nurses",
] as const;

function PhoneIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <path d="M8.3 4.8 10 7.85a2.45 2.45 0 0 1-.5 2.9l-.75.75a14.2 14.2 0 0 0 3.75 3.75l.75-.75a2.45 2.45 0 0 1 2.9-.5l3.05 1.7a1.8 1.8 0 0 1 .85 2.05c-.3 1.25-1.3 2.15-2.6 2.25-2.7.2-6.65-1.25-9.7-4.3C4.7 12.65 3.25 8.7 3.45 6c.1-1.3 1-2.3 2.25-2.6a1.8 1.8 0 0 1 2.6.85v.55Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.65" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
      <rect height="16" rx="3" stroke="currentColor" strokeWidth="1.7" width="17" x="3.5" y="5" />
      <path d="M7.5 3v4M16.5 3v4M3.5 9.5h17m-5.5 4-3.75 3.75L9.5 15.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" />
    </svg>
  );
}

export function MarketingFooter({ appStoreUrl, googlePlayUrl, showInquiry = true }: MarketingFooterProps) {
  return (
    <footer className={styles.footer} id="contact">
      <div className={`${styles.inner} container`}>
        <div className={styles.primaryGrid}>
          <section className={styles.brandColumn}>
            <Link className={styles.brand} href="/#home">
              <CarevincoLogo />
            </Link>
            <div className={styles.socials}>
              {SOCIAL_ICONS.map((icon) => (
                <span aria-hidden="true" key={icon.alt} title={icon.alt}>
                  <Image
                    alt=""
                    height={icon.height}
                    src={icon.src}
                    style={{ height: icon.height, width: icon.width }}
                    width={icon.width}
                  />
                </span>
              ))}
              {isSiteActive ? (
                <a aria-label={`Message Carevinco on WhatsApp at ${customerSupportWhatsApp}`} href={customerSupportWhatsAppHref} rel="noopener noreferrer" target="_blank">
                  <Image
                    alt=""
                    height={34}
                    src="/assets/landingPage/whats-app-dbf5a2cf-627d-4544-bc68-f2388a3f1573.png"
                    style={{ height: 34, width: 34 }}
                    width={34}
                  />
                </a>
              ) : null}
            </div>
            <p>Trusted home nursing in {seoLocality.displayName}, delivering compassionate care with confidence and peace of mind.</p>
          </section>

          <section className={styles.linkColumn}>
            <h2>Support</h2>
            <nav aria-label="Carevinco support links">
              {isSiteActive ? <a href={customerSupportTelHref}>Call Customer Care</a> : null}
              {isSiteActive ? <a href={customerSupportWhatsAppHref} rel="noopener noreferrer" target="_blank">WhatsApp Support</a> : null}
              <a href={customerSupportEmailHref}>Email Support</a>
            </nav>
          </section>

          <section className={styles.linkColumn}>
            <h2>Legal</h2>
            <nav aria-label="Carevinco legal links">
              <Link href="/terms-and-conditions">Terms &amp; Conditions</Link>
              <Link href="/privacy-policy">Privacy Policy</Link>
              <Link href="/account-deletion">Delete Account</Link>
            </nav>
          </section>

          <section className={styles.contactColumn}>
            <h2>Get In Touch</h2>
            {isSiteActive ? <a href={customerSupportTelHref}><strong>{customerSupportPhone}</strong><span>Customer Care</span></a> : null}
            {isSiteActive ? <a href={customerSupportWhatsAppHref} rel="noopener noreferrer" target="_blank"><strong>{customerSupportWhatsApp}</strong><span>WhatsApp Support</span></a> : null}
            <a href={customerSupportEmailHref}><strong>{customerSupportEmail}</strong><span>Email</span></a>
          </section>
        </div>

        {showInquiry ? (
          <div className={styles.serviceRow}>
            <section>
              <h2>Service City</h2>
              <div className={styles.chips}>{SERVICE_CITIES.map((city) => <span key={city}>{city}</span>)}</div>
            </section>
            <section className={styles.footerCta}>
              <h2>Need Trusted Home Nursing Care For Your Loved One?</h2>
              <p>{footerCareDescription}</p>
              <div className={styles.actions}>
                {isSiteActive ? <a href={customerSupportTelHref}><PhoneIcon />{marketingActions.talkToExpert}</a> : null}
                <button data-open-inquiry type="button"><CalendarIcon />{marketingActions.bookVisit}</button>
              </div>
            </section>
          </div>
        ) : null}

        <div className={styles.promiseRow}>
          <section>
            <h2>Our Promise: Your Care, Our Commitment.</h2>
            <p>{PROMISE_KEYWORDS.join(" | ")}</p>
          </section>
          <section className={styles.storeColumn}>
            <div>
              <a aria-label="Download Carevinco on Google Play" href={googlePlayUrl} rel="noopener noreferrer" target="_blank">
                <Image alt="Get it on Google Play" height={50} src="/assets/landingPage/get-it-on-google-play-6f3745f5-3193-435f-86ee-3a52e9f79847.png" width={168} />
              </a>
              <a aria-label="Download Carevinco on the App Store" href={appStoreUrl} rel="noopener noreferrer" target="_blank">
                <Image alt="Download on the App Store" height={50} src="/assets/landingPage/download-on-app-store-59f980cd-a91a-4a77-b798-9cae570c83d4.png" width={168} />
              </a>
            </div>
            <p>{appDownloadDescription}</p>
          </section>
        </div>
      </div>
      <div className={styles.copyright}>© {new Date().getFullYear()} Carevinco. All rights reserved.</div>
    </footer>
  );
}
